'use client';

import { c as _c } from "react/compiler-runtime";
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { UnauthQueryOptions } from '@/api';
import { SearchSideAdBanner } from '@/app/_components/ads';
import { FestivalCard } from '@/app/(allowed)/festival/_components/FestivalCard';
import { useRegionMetaData } from '@/components/client/RegionSelect/useRegionMetaData';
import { DesktopPageShell } from '@/components/template/DesktopPageShell';
import { NATIONWIDE_FESTIVAL_RADIUS_METER } from '@/constants/festival';
import { DEFAULT_LOCATION } from '@/constants/geo';
import { PAGE_SIZE } from '../../constants';
import { SearchEmptyResults } from '../SearchEmptyResults';
import { SearchSectionShell } from '../SearchSectionShell';
import { FestivalCardSkeleton } from './FestivalCardSkeleton';
import { FestivalFilter } from './FestivalFilter';
const FESTIVAL_GRID_CLASSNAME = 'm-0 grid list-none gap-x-12 gap-y-32 p-0 pb-40 mobile:grid-cols-1 tablet:grid-cols-2 xl:grid-cols-2 mobile-tablet:px-20';
interface FestivalSearchProps {
  keyword: string;
  page: number;
  onPageChange: (page: number) => void;
  isFilterOpen: boolean;
  onCloseFilter: () => void;
  lclsSystm2: string | null;
  onSelectLclsSystm2: (code: string | null) => void;
  regionCode: string | null;
  subRegionCode: string | null;
  onSelectRegion: (code: string | null) => void;
  onSelectSubRegion: (code: string | null) => void;
  radius: number;
  onSelectRadius: (radius: number | null) => void;
  month: number | null;
  onSelectMonth: (month: number | null) => void;
  onReset: () => void;
}
export function FestivalSearch(t0) {
  const $ = _c(43);
  const {
    keyword,
    page,
    onPageChange,
    isFilterOpen,
    onCloseFilter,
    lclsSystm2,
    onSelectLclsSystm2,
    regionCode,
    subRegionCode,
    onSelectRegion,
    onSelectSubRegion,
    radius,
    onSelectRadius,
    month,
    onSelectMonth,
    onReset
  } = t0;
  const regionMetaDataQuery = useRegionMetaData();
  let t1;
  let t2;
  if ($[0] !== keyword || $[1] !== lclsSystm2 || $[2] !== month || $[3] !== page || $[4] !== radius || $[5] !== regionCode || $[6] !== regionMetaDataQuery || $[7] !== subRegionCode) {
    const distanceSubRegionCode = subRegionCode ?? (regionCode ? regionMetaDataQuery.data?.subRegionByParent.get(regionCode)?.[0]?.code : undefined) ?? DEFAULT_LOCATION.codeSubRegion;
    const effectiveRadius = subRegionCode ? radius : NATIONWIDE_FESTIVAL_RADIUS_METER;
    t2 = useQuery;
    t1 = UnauthQueryOptions["get:/wello/v2/allows/query/tour/festival"]({
      query: {
        subRegionCode: distanceSubRegionCode,
        radius: effectiveRadius,
        searchKeyword: keyword || undefined,
        lclsSystm2: lclsSystm2 || undefined,
        date: month ? dayjs().date(1).month(month - 1).format("YYYY-MM-DD") : undefined,
        ...(regionCode ? {
          searchRegionCode: regionCode
        } : {}),
        ...(subRegionCode ? {
          searchSubRegionCode: subRegionCode
        } : {}),
        page,
        size: PAGE_SIZE
      }
    });
    $[0] = keyword;
    $[1] = lclsSystm2;
    $[2] = month;
    $[3] = page;
    $[4] = radius;
    $[5] = regionCode;
    $[6] = regionMetaDataQuery;
    $[7] = subRegionCode;
    $[8] = t1;
    $[9] = t2;
  } else {
    t1 = $[8];
    t2 = $[9];
  }
  let t3;
  if ($[10] !== t1) {
    t3 = {
      ...t1,
      placeholderData: keepPreviousData
    };
    $[10] = t1;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const {
    data,
    isLoading
  } = t2(t3);
  let t4;
  if ($[12] !== data?.context?.content) {
    t4 = data?.context?.content ?? [];
    $[12] = data?.context?.content;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const festivals = t4;
  const t5 = data?.context?.totalElements ?? 0;
  const t6 = data?.context?.totalPages ?? 0;
  let t7;
  if ($[14] !== t5 || $[15] !== t6) {
    t7 = {
      totalCount: t5,
      totalPages: t6
    };
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const meta = t7;
  let t8;
  if ($[17] !== isFilterOpen || $[18] !== lclsSystm2 || $[19] !== month || $[20] !== onCloseFilter || $[21] !== onReset || $[22] !== onSelectLclsSystm2 || $[23] !== onSelectMonth || $[24] !== onSelectRadius || $[25] !== onSelectRegion || $[26] !== onSelectSubRegion || $[27] !== radius || $[28] !== regionCode || $[29] !== subRegionCode) {
    t8 = <FestivalFilter isDrawerOpen={isFilterOpen} onCloseDrawer={onCloseFilter} onApplyDrawer={onCloseFilter} lclsSystm2={lclsSystm2} onSelectLclsSystm2={onSelectLclsSystm2} regionCode={regionCode} subRegionCode={subRegionCode} onSelectRegion={onSelectRegion} onSelectSubRegion={onSelectSubRegion} radius={radius} onSelectRadius={onSelectRadius} month={month} onSelectMonth={onSelectMonth} onReset={onReset} />;
    $[17] = isFilterOpen;
    $[18] = lclsSystm2;
    $[19] = month;
    $[20] = onCloseFilter;
    $[21] = onReset;
    $[22] = onSelectLclsSystm2;
    $[23] = onSelectMonth;
    $[24] = onSelectRadius;
    $[25] = onSelectRegion;
    $[26] = onSelectSubRegion;
    $[27] = radius;
    $[28] = regionCode;
    $[29] = subRegionCode;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  let t9;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <SearchSideAdBanner />;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  let t10;
  if ($[32] !== festivals || $[33] !== isLoading) {
    t10 = isLoading ? <ul className={FESTIVAL_GRID_CLASSNAME}>{Array.from({
        length: 4
      }, _temp)}</ul> : festivals.length < 1 ? <SearchEmptyResults /> : <ul className={FESTIVAL_GRID_CLASSNAME}>{festivals.map(_temp2)}</ul>;
    $[32] = festivals;
    $[33] = isLoading;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== meta || $[36] !== onPageChange || $[37] !== page || $[38] !== t10) {
    t11 = <SearchSectionShell meta={meta} page={page} pageSize={PAGE_SIZE} onPageChange={onPageChange}>{t10}</SearchSectionShell>;
    $[35] = meta;
    $[36] = onPageChange;
    $[37] = page;
    $[38] = t10;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  let t12;
  if ($[40] !== t11 || $[41] !== t8) {
    t12 = <DesktopPageShell className="mt-20 w-full mobile-tablet:mt-0" leftAside={t8} rightAside={t9}>{t11}</DesktopPageShell>;
    $[40] = t11;
    $[41] = t8;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  return t12;
}
function _temp2(festival, index_0) {
  return <li key={festival.id ?? index_0}><FestivalCard festival={festival} /></li>;
}
function _temp(_, index) {
  return <li key={index}><FestivalCardSkeleton /></li>;
}